import { Fragment, useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { EmailLink } from '@/components/ui/TextLink';
import { ChangeSignerDialog } from '@/components/docusign/ChangeSignerDialog';
import { EnvelopeStatusBadge } from '@/components/docusign/EnvelopeStatusBadge';
import { RemindEnvelopeControl } from '@/components/docusign/RemindEnvelopeControl';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **Five imports were dropped on 2026-08-30 when the two notice boxes were
 * removed** — `AlertIcon`, `BACKGROUND_CHECK_NOTE`, `STATUS_IS_LAST_KNOWN_NOTE`,
 * `signatureConsequenceNote` and `signaturesNotBinding`. **All five still EXIST
 * and are still exported**; nothing was deleted from `@/lib/docusign`, and other
 * surfaces still read them. Their absence here is this panel no longer rendering
 * that copy, never a sign the helpers went away.
 */
import {
  isEmbeddedDelivery,
  signingUrlRefusal,
  type DocuSignBindingSignals,
  type SignerValue,
} from '@/lib/docusign';
import { formatDateTime } from '@/lib/format';
import type { DocuSignEnvelope, DocuSignSignedFields } from '@/lib/types';
/*
 * ⚠️ **The list read and its state LEFT THIS FILE on 2026-09-03** and now live
 * in `useQuoteEnvelopes`. They were lifted because the quote page's HEADER
 * offers the same Send / Resend / Change-signer controls this panel does, and
 * it cannot tell those apart without knowing whether an envelope is
 * outstanding — an answer that was locked inside this component's `useState`.
 *
 * Nothing about WHAT is read changed: the same request, the same `perPage`
 * ceiling, the same deliberately-unsent `order`, the same 401 and 404 handling,
 * and the same refusal to drop a previous list to show an error. The hook's
 * header carries every one of those reasons; read it before changing this
 * panel's list behaviour, because the reason for each now lives there.
 */
import { useQuoteEnvelopes } from '@/lib/use-quote-envelopes';
import { Badge, Button, EmptyState, Panel, PanelBody, PanelHeader, Skeleton } from '@/components/ui';

/*
 * The signature envelopes raised against ONE quote.
 *
 * Modelled on `XeroPanel` — a record-level panel that reads its own slice, says
 * where things stand, and offers the actions that apply. The differences from
 * that panel are the two things this integration gets wrong most easily:
 *
 * ⚠️ **THERE IS NO WEBHOOK.** Nothing pushes a status change to us. The stored
 * status is only as current as the last `POST /docusign/envelopes/{id}/refresh`,
 * which is why every timestamp on this panel is worded as *last checked* and
 * never as *status*, and why the check is an explicit button rather than
 * something the panel does on a timer. A screen that reads as live tracking
 * presents "nothing has asked since Tuesday" as "we asked just now".
 *
 * ⚠️ **A SERVER-SIDE POLL EXISTS, and it does NOT weaken the paragraph above.**
 * The backend re-checks outstanding envelopes on a timer — see
 * `BACKGROUND_CHECK_NOTE` in `@/lib/docusign` for the verified figures and the
 * three caveats. What that changes is the ARGUMENT, not the rule: the reason
 * this panel may not look live is no longer "nothing will ever happen without a
 * person", it is that **what you are reading is a snapshot from the last check
 * and the next one is minutes away and not guaranteed.** Restating the rule with
 * its dead reason is how it gets deleted by the next reader.
 *
 * ⚠️ **A COMPLETED SIGNATURE ACCEPTS THE QUOTE**, moving it to the accepted
 * stage with no confirmation step, because by then nobody is at a screen.
 * Acceptance moves the stage and nothing more — billing an accepted quote is a
 * person's job afterwards.
 *
 * ⚠️ **THE PANEL NO LONGER SAYS SO — owner's instruction, 2026-08-30.** This
 * paragraph used to continue: *"…so it is stated in the body ABOVE the list and
 * above the Send control, in every state including the empty one."* That block,
 * the sandbox warning beside it and the panel `description` were all removed;
 * the send screen's matching notice went too. **The FACTS above are unchanged —
 * only the copy is gone**, and the removal comment further down says which box
 * carried what. Do not read the silence as the behaviour having changed, and do
 * not restore the boxes as an oversight.
 *
 * ⚠️ **AND THE CHECK NOW NEEDS A WRITE ROLE — 2026-08-29, reversing a decision
 * this file used to record at length.** Release `20260829123506-c323947` put
 * `WRITE_ROLES` on `POST /docusign/envelopes/{id}/refresh`, so all four controls
 * here sit behind `canManage`; the reversal and the owner's original reasoning
 * are written out on the button in `EnvelopeRow`.
 *
 * ⚠️ **THAT GATE'S PRICE WAS PAID BY THE POLLER, and this paragraph used to
 * claim otherwise.** What stood here was: *"there is no webhook, and now the
 * people who can trigger the only check are a subset of the people who can read
 * this panel. A quote the customer signed sits unaccepted until somebody with a
 * write role opens it and presses Check status."* **False as of the background
 * worker** — the server runs the same check on a timer, with no role and nobody
 * at a screen, so gating the button no longer strands a signed quote.
 *
 * The half that survives: a signature still does not take effect *instantly* and
 * this panel still may not be made to look live. Say that because the next check
 * is minutes away and not guaranteed, **not** because nothing will happen
 * without a person — that reason is spent.
 *
 * ⚠️ **Acceptance has still never been OBSERVED here.** A `200` from the refresh
 * proves only that the envelope was read — see `check()` — so this panel states
 * the consequence BEFORE a send, where it is a warning, and reports none of it
 * AFTER a check, where it would be a claim it cannot support.
 *
 * Nothing here talks to DocuSign. Every call goes through `@/lib/api` to our own
 * API, which holds the RSA key and does the JWT grant; see `@/lib/docusign`.
 *
 * **The dialogs are not ours.** Sending, minting a signing link and voiding each
 * have their own screen with their own validation, owned elsewhere; this panel
 * only asks for them through callbacks. A callback that is not passed means the
 * button is not offered at all, which is honest — the alternative is a control
 * that opens nothing.
 */

export interface EnvelopePanelProps {
  /** The quote these envelopes settle. `GET /docusign/envelopes?quoteId=N`. */
  quoteId: number;
  /**
   * How to name the quote in copy — its `quoteNumber`, or the subject where
   * there is no number yet. Only ever rendered as text.
   *
   * ⚠️ **Currently READ BY NOTHING**, and kept deliberately. Its only reader was
   * the acceptance notice removed on 2026-08-30; the prop stays so the call site
   * is unaffected and so any future sentence naming the quote has it. It is not
   * dead by accident — see the note where it would be destructured.
   */
  quoteLabel: string;
  /**
   * The quote's company, for the signer picker inside "Change signer and
   * resend". `null` when the quote names none — the picker then offers nothing
   * and says why, rather than falling back to the unscoped directory of every
   * contact in the CRM. See `@/lib/contact-scope`.
   *
   * ⚠️ **Not derivable from an envelope.** `DocuSignEnvelope.quote` is
   * `{id, quoteNumber, subject}` and carries no company at all, so this has to
   * come from the page that already holds the quote.
   */
  companyId?: number | null;
  /**
   * The DocuSign connection.
   *
   * ⚠️ **It no longer feeds a not-binding warning on this panel** — that block
   * was removed on 2026-08-30. What still reads it is `EnvelopeStatusBadge` on
   * each row, which uses it to qualify `completed` rather than let it read as an
   * unqualified success. So the prop is still load-bearing, for a different
   * consumer than it was written for.
   *
   * ⚠️ **Optional, and it has to be: `GET /docusign/connection` is ADMIN
   * ONLY**, so on a quote page most signed-in people cannot read it and this
   * panel must not try. Absent, `signaturesNotBinding()` answers `true` and the
   * badge qualifies in its unconfirmed wording — which is the correct answer in
   * the moment we know least, not a degradation. Typed structurally so a page
   * that holds only a partial connection can still pass it.
   */
  connection?: DocuSignBindingSignals | null;
  /**
   * Gate writes. Pass `ability.canUpdate('quotes')` from the call site.
   *
   * ⚠️ **As of 2026-08-29 this gates FOUR controls, not three.** Send, the
   * signing link and Void have always sat behind it; **Check status now does
   * too** — backend release `20260829123506-c323947` put a write role on
   * `POST /docusign/envelopes/{id}/refresh`, so a `viewer` pressing it collects
   * a `403`. See the reversal recorded on the button itself.
   *
   * Never `user.role`, never `/permissions/me` — the ability model is the one
   * place that reconciles the two.
   */
  canManage: boolean;
  /**
   * The true reason a write is refused, rendered as text beside the control as
   * well as on its `title` — a disabled button has `pointer-events: none`, so
   * its tooltip never reaches the person it was written for.
   */
  manageDenial?: string | null;
  /**
   * Opens the send-for-signature dialog. Owned by another screen: it picks a
   * template, names a signer and chooses `delivery`, and `delivery: 'email'`
   * writes to a customer the moment it succeeds.
   */
  onSend?: () => void;
  /**
   * Mints a single-use signing link for one envelope.
   *
   * ⚠️ **The panel never holds a URL.** A signing link is single use and dies in
   * about five minutes, so it is minted with the signer in front of you and
   * handed straight over. Storing one, or rendering one this panel keeps,
   * produces a dead link by the time anybody clicks it — and a live credential
   * sitting in a page. The dialog owns the whole of that.
   */
  onRequestSigningLink?: (envelope: DocuSignEnvelope) => void;
  /**
   * Voids one envelope. There is no delete and no unsend: voiding marks it dead
   * and DocuSign notifies whoever was asked to sign, quoting the reason, which
   * is why the reason belongs in a dialog rather than in a confirm here.
   */
  onVoid?: (envelope: DocuSignEnvelope) => void;
  /**
   * "Change signer and resend" — called AFTER this panel has voided the old
   * envelope, with the signer the user chose.
   *
   * ⚠️ **The void has already happened by the time this fires.** The panel owns
   * that half because `ChangeSignerDialog` is the screen that states its cost;
   * what the panel cannot own is the SEND, which needs the whole document
   * payload — the anchor text, the Xero fetch or an uploaded PDF — and belongs to
   * `SendForSignatureModal`. So the caller's job is exactly one thing: open that
   * dialog with `initialSigner` set to the value handed here.
   *
   * ⚠️ **Dropping the signer is the failure this signature exists to prevent.**
   * The send dialog seeds the company's primary contact into an untouched signer
   * field, and that is very often the person somebody has just decided not to
   * send to — so opening it without `initialSigner` silently undoes the whole
   * operation while looking like it worked.
   *
   * Not passed means the control is not offered at all, which is honest; the
   * alternative is a button that voids a live envelope and then opens nothing.
   */
  onChangeSigner?: (envelope: DocuSignEnvelope, signer: SignerValue) => void;
  /**
   * Called after a check that came back signed.
   *
   * ⚠️ **Not cosmetic.** That same request accepts the quote server-side — it
   * moves to the accepting stage — so the quote on screen may be stale the
   * instant this fires. The page has to re-read it; this panel cannot, because
   * it does not own the quote. The stage move is the whole effect — no
   * invoice, no Xero push.
   *
   * ⚠️ **"May be", not "is", and that difference is the whole reason to
   * re-read.** The acceptance is best-effort server-side, and a `200` proves
   * only that the envelope was read — see `check()`. So neither this panel nor
   * its caller can tell from the response whether the quote actually moved;
   * re-reading is how that is learnt, not a refresh of something already known.
   * Never phrase this callback's effect as an accomplished fact in copy.
   */
  onSigned?: () => void;
  /**
   * Bump to force a re-read of the list — after the send dialog closes with a
   * `201`, or the void dialog with a `200`. Those dialogs are not ours, so they
   * cannot hand us the row.
   */
  reloadKey?: number;
}

/*
 * ⚠️ **`ListState` and `IDLE` LIVED HERE and MOVED, 2026-09-03** — they are
 * `QuoteEnvelopesState` and the hook's own `IDLE` in `@/lib/use-quote-envelopes`
 * now, with every doc comment carried across. They were not deleted, and a grep
 * that finds no `ListState` in this file has found a move, not a removal.
 *
 * ⚠️ **ONE MEMBER CHANGED IN THE MOVE, and it is worth knowing about here:
 * `envelopes` is `DocuSignEnvelope[] | null`, where this file had a
 * non-nullable array starting at `[]`.** `null` means *nothing has been read* —
 * first load, a failed read, or an API with no DocuSign half. This panel cannot
 * tell the difference on screen, because `loading` and `notWired` own their own
 * branches above the list; the quote HEADER can and must, because it decides
 * between offering Send and offering Resend from it. See the hook.
 */

/**
 * A failed status check, kept as three fields rather than one string.
 *
 * ⚠️ **`advice` is rendered BESIDE `message`, never instead of it.** Both are
 * true at once — the server said why it refused, and we know what a reader can
 * do about it — so joining them with `??` would silently drop whichever came
 * second. That is the `a ?? b` rule in CLAUDE.md, and a refusal is exactly where
 * dropping half of it costs the most.
 */
interface CheckFailure {
  /** Ours: what failed. */
  title: string;
  /** The server's own sentence, verbatim. Never paraphrased, never replaced. */
  message: string;
  /** Ours: what to do next, where there is something to do. */
  advice: string | null;
}

/** Empty-state glyph: a page with a signature across it. */
function EnvelopeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M5 3h9l5 5v6" />
      <path d="M14 3v5h5" />
      <path d="M4 20c2 0 2-6 3.5-6S9 19 11 19c1 0 1.6-.6 2-1.4" />
    </svg>
  );
}

/** A labelled fact. Pairs rather than table rows, so one column works at 320px. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-medium uppercase tracking-[0.08em] text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

/** A timestamp, or a sentence saying there isn't one. Never a bare dash for a check. */
function checkedLabel(iso: string | null | undefined): string {
  return iso ? `Last checked ${formatDateTime(iso)}` : 'Never checked with DocuSign';
}

function dateOrDash(iso: string | null | undefined): string {
  return iso ? formatDateTime(iso) : '—';
}

/**
 * One value the signer typed on the document, or the fact that they left that
 * box empty.
 *
 * ⚠️ **Deliberately NOT `dateOrDash`/`orDash`.** Both boxes are optional, so an
 * empty one is an ANSWER rather than missing data, and an em dash reads as the
 * second. It is also the only thing separating it on screen from the other
 * null on this shape — `signedFields` itself being absent, which means nothing
 * has been read back and is rendered by its own branch, not by this. See
 * {@link DocuSignSignedFields}.
 *
 * Subdued, never a warning tone: nothing has gone wrong when a customer skips
 * an optional box. Empty-after-trim counts as blank, because a value made of
 * spaces renders as an unexplained gap.
 */
function typedValue(value: string | null): ReactNode {
  return value && value.trim() !== '' ? (
    value
  ) : (
    <span className="text-fg-subtle">Left blank</span>
  );
}

/*
 * ⚠️ **`AcceptanceConsequence` and `NotBindingWarning` LIVED HERE and were
 * REMOVED on the owner's instruction, 2026-08-30**, together with
 * `notBindingHeading`. Both were standing explanatory blocks rendered above the
 * envelope list; neither reported a server response. The matching notice on the
 * send screen went with them (`SendForSignatureModal`).
 *
 * **Neither removal changes a fact, and both facts still hold:**
 *
 * - **Production runs the DocuSign SANDBOX, deliberately.** Envelopes raised
 *   against a quote are **not legally binding**, and **the failure mode looks
 *   exactly like success** — a signer completes one, it comes back marked
 *   signed, and it is worth nothing. `signaturesNotBinding()` in
 *   `@/lib/docusign` is untouched and remains the one rule; the send screen
 *   still renders its own sandbox warning, which was NOT part of this removal.
 * - **A completed signature still accepts the quote**, moving it to the
 *   accepted stage with no confirmation step, and the backend's status worker
 *   still polls outstanding envelopes on a timer — so it still happens **with
 *   nobody on this screen**. Acceptance moves the stage and nothing more.
 *   `signatureConsequenceNote()`, `SIGNATURE_ACCEPTS_QUOTE_NOTE` and
 *   `BACKGROUND_CHECK_NOTE` are untouched in `@/lib/docusign` and still carry
 *   the wording for any surface that needs it.
 *
 * **Do not restore either as an oversight** — this was a decision, and the
 * comment exists so the next reader can tell the difference. What stays, and
 * was never in scope: every error and refusal block, the `403` branch on the
 * status check, and the Check-status denial reason.
 */

/**
 * A failure worth someone's attention, named rather than described.
 *
 * `note` is an optional SECOND paragraph, for the cases where we know what a
 * reader can do about the refusal. It never displaces `children`, which is the
 * server's own sentence — see `CheckFailure`.
 */
function ErrorBlock({
  title,
  children,
  note,
}: {
  title: string;
  children: ReactNode;
  note?: string | null;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-1 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger"
    >
      <p className="m-0 font-semibold">{title}</p>
      <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{children}</p>
      {note && <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{note}</p>}
    </div>
  );
}

/** The loading shape: two envelope cards, not a spinner. */
function EnvelopeSkeletons() {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      {[0, 1].map((row) => (
        <div key={row} className="flex flex-col gap-2 rounded-md border border-border px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-28" />
          </div>
          <Skeleton className="h-3 w-40" />
          <div className="grid gap-2 sm:grid-cols-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EnvelopePanel({
  quoteId,
  /*
   * ⚠️ `quoteLabel` is deliberately NOT destructured. It is still a declared,
   * still-passed prop (see its doc on `EnvelopePanelProps`), but the only thing
   * that read it was the acceptance notice removed on 2026-08-30. The prop is
   * kept so the call site is unaffected and so a future sentence naming the
   * quote has it to hand; it is not dead by accident.
   */
  companyId = null,
  connection,
  canManage,
  manageDenial,
  onSend,
  onRequestSigningLink,
  onVoid,
  onChangeSigner,
  onSigned,
  reloadKey = 0,
}: EnvelopePanelProps) {
  const { envelopes, loading, error, notWired } = useQuoteEnvelopes(quoteId, reloadKey);
  /**
   * Rows this panel has been handed a NEWER copy of than the last list read
   * holds — a status check, a reminder, or the void behind "change signer and
   * resend". Each of those responses IS the server's own copy of the envelope,
   * so it is written straight in; re-fetching would spend a request to learn
   * what was just returned.
   *
   * ⚠️ **A patch OVER the hook's list, never a second list.** It holds only the
   * rows that have moved since the read, keyed by id, and it is DISCARDED the
   * moment a fresh read lands — see where `rows` is built. Without that, a
   * `reloadKey` bump would fetch the current truth and then have a stale patch
   * drawn back over the top of it, with a `200` everywhere and nothing to fail.
   */
  const [patches, setPatches] = useState<Record<number, DocuSignEnvelope | undefined>>({});
  /**
   * The list read those patches were taken against, so a new one can be
   * recognised. Held as state rather than a ref because it is read during
   * render — see `rows`.
   */
  const [patchedAgainst, setPatchedAgainst] = useState<DocuSignEnvelope[] | null>(envelopes);
  /**
   * The envelope whose signer is being changed. Held here rather than on the row
   * because the dialog is portalled and must survive the row re-rendering as the
   * list refreshes underneath it.
   */
  const [changingSigner, setChangingSigner] = useState<DocuSignEnvelope | null>(null);
  /** Which envelope is being checked. One at a time, so a value rather than a set. */
  const [checkingId, setCheckingId] = useState<number | null>(null);
  /**
   * A failed check, kept apart from the list's own error: the list is fine, one
   * request against DocuSign was not, and conflating them would say the panel
   * could not be read when it plainly was.
   */
  const [checkError, setCheckError] = useState<CheckFailure | null>(null);
  const headingId = useId();

  /*
   * State lags a rapid second click by a render; a ref is read synchronously.
   * A double-checked envelope spends the hourly DocuSign quota twice, and on a
   * completed one it re-runs the stage move and the Xero attachment.
   */
  const inFlight = useRef(false);

  /*
   * ⚠️ **THE FETCH EFFECT LIVED HERE and moved to `useQuoteEnvelopes` on
   * 2026-09-03**, unchanged apart from the `null`-on-404 note recorded there.
   * Its reasoning went with it — the `AbortSignal` guarded in both `.catch` and
   * `.finally`, the absent `setState({loading: true})`, the unsent `order`, the
   * `perPage` ceiling, the 401 left to `api.ts`, the calm 404, and the previous
   * list kept on screen through a failed re-read. **Read the hook before
   * changing any of it**; none of those decisions was re-taken in the move.
   */

  /**
   * Ask DocuSign where one envelope stands, and write the answer back.
   *
   * ⚠️ **SUPERSEDED 2026-08-30: *"This is the only way a status ever changes …
   * nothing polls"*.** Something does. The backend re-checks outstanding
   * envelopes on a timer and runs this same route, so a row can change without
   * anybody pressing anything — see `BACKGROUND_CHECK_NOTE` in `@/lib/docusign`.
   *
   * What is still true, and is why this button exists: there is no webhook,
   * **nothing in this browser polls**, and the row on screen is exactly as
   * current as the last check — whoever or whatever ran it. This is how a reader
   * gets an answer *now* rather than the only way to get one at all.
   *
   * ⚠️ **IT NEEDS A WRITE ROLE as of 2026-08-29, and a `403` is a state this
   * function must handle rather than a state the button prevents.** The gate on
   * the button is an optimisation — an access token lives about five days and
   * carries the role it was issued with, so a role can be taken away underneath
   * a session that is still rendering an enabled control. The `403` carries **no
   * `details`**, so it renders as a panel-level block and never against a field;
   * there is no field here to attach it to in any case.
   *
   * It is offered on terminal envelopes too, and that is deliberate rather than
   * an oversight: a check that observes `completed` also moves the quote to the
   * accepted stage, best-effort and idempotent. So checking a signed envelope
   * again is the repair for a quote that was signed and still reads as unsigned.
   *
   * ⚠️ **WHAT A `200` HERE ACTUALLY MEANS: the envelope was read.** Nothing
   * more. The acceptance behind it is best-effort and logs rather than throws,
   * so a `200` does not prove the quote moved. **This panel must never report a
   * consequence it did not observe.** It updates the row it was handed and asks
   * the page to re-read the quote, which is the only way to learn what really
   * happened.
   *
   * ⚠️ **There is deliberately no user-facing copy about whether the stage move
   * actually ran.** That is our uncertainty, not the reader's: a panel that
   * hedges about whether the server did its job teaches people to distrust a
   * status that is usually right. Do not read the absence of reported failures
   * as proof that it worked.
   */
  const check = useCallback(
    async (envelope: DocuSignEnvelope) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setCheckingId(envelope.id);
      setCheckError(null);
      try {
        /*
         * ⚠️ **Only `data` is read, and as of 2026-08-29 there is nothing else
         * on the response to read.**
         *
         * ⚠️ **DELETED 2026-08-29: a forward-looking `meta.acceptance` block.**
         * What stood here sketched a shape read out of the backend's working
         * tree — `meta.acceptance: { quoteId, billingMode, invoice: {id,
         * created} | null, xero: {...}, problem } | null` — as the answer to the
         * question this panel cannot ask: did the acceptance chain run, and what
         * did it do? It was kept unbuilt on the grounds that a shape in
         * somebody's `src/` is not a shape on the wire. **The backend has now
         * confirmed it is not coming**: release `20260829011913-5fbab06` deleted
         * the acceptance chain, `meta` is absent from this response entirely,
         * and a signature raises nothing for it to describe. So the block goes,
         * rather than sitting here as a plan for a response that will never
         * arrive — a stale sketch is read as a roadmap.
         *
         * ⚠️ **The REASONING outlived it and is kept, in CLAUDE.md's
         * proxy-field table.** `invoice.created` was the sharpest entry there:
         * *an invoice being non-null only proves an invoice EXISTS; only
         * `created: true` proves THIS CALL made it.* That is a property of any
         * best-effort, re-runnable hook reporting on a record, not of this
         * response — the same shape as reading `xeroQuoteNumber` as an "is this
         * in Xero?" test. It is filed under **THE PROXY-FIELD PATTERN** there;
         * do not re-derive it here when the next such field appears.
         *
         * The half of the old note that was never about `meta` still stands, and
         * this repo has paid for it three times — `quote-acceptance.ts`,
         * migration 075, the product-inclusions router: **build against the
         * wire, not against their working tree.** Measure the artefact.
         */
        const { data } = await api.refreshDocusignEnvelope(envelope.id);
        /*
         * ⚠️ **Written as a PATCH over the hook's list, not into it.** The list
         * belongs to `useQuoteEnvelopes` now and this panel cannot reach inside
         * it; what it can do is hold the newer copy the server just handed back
         * — which is exactly what this response is. A `reloadKey` re-read
         * supersedes it. See `patches`.
         */
        setPatches((current) => ({ ...current, [data.id]: data }));
        /*
         * The same request ATTEMPTED to accept the quote server-side, so the
         * quote on screen may be stale from this moment and only the page can
         * re-read it.
         *
         * Worded as an attempt rather than an outcome deliberately: all we were
         * told is that the envelope is signed — see the `200` caveat on this
         * function — so the re-read is how the page LEARNS what happened, not a
         * refresh of something already known.
         */
        if (data.signed) onSigned?.();
      } catch (caught) {
        /*
         * A banner, never per-field errors: a 403 and a 502 from DocuSign both
         * arrive with no `details` at all, and there is no input here to attach
         * anything to.
         *
         * ⚠️ **401 is left to `api.ts`**, which attempts one refresh and then
         * ends the session; a message here would contradict it mid sign-out.
         * Same handling as the list read above.
         */
        if (caught instanceof ApiError && caught.status === 401) return;
        /*
         * ⚠️ **The 403 is not a bug and is not unreachable.** From 2026-08-29
         * this endpoint needs a write role, and the button is gated on the same
         * ability — so reaching here means the two disagree, which is exactly
         * what a role changed under a live token looks like. Naming that is the
         * difference between a control that refused and a control that broke.
         *
         * The server's own sentence is still rendered; ours goes BESIDE it, as
         * `advice`, because both are true and a `??` between them drops one.
         */
        if (caught instanceof ApiError && caught.status === 403) {
          setCheckError({
            title: 'DocuSign checks need a role that can update quotes.',
            message: caught.formMessage ?? caught.message,
            advice: 'Sign in again to refresh your access level.',
          });
          return;
        }
        setCheckError({
          title: 'Couldn’t check the status with DocuSign.',
          message:
            caught instanceof ApiError
              ? (caught.formMessage ?? caught.message)
              : caught instanceof Error
                ? caught.message
                : 'The check with DocuSign didn’t complete.',
          advice: null,
        });
      } finally {
        inFlight.current = false;
        setCheckingId(null);
      }
    },
    [onSigned],
  );

  /**
   * Swap one row for the server's own copy of it.
   *
   * Used by the reminder (which moves `lastRemindedAt` and nothing else) and by
   * the change-signer void (which returns the envelope re-read from DocuSign
   * rather than assumed `voided`). Replacing beats re-fetching: the response IS
   * the answer, and a re-read would spend a request to learn what was just
   * returned.
   */
  const replaceRow = useCallback((next: DocuSignEnvelope) => {
    setPatches((current) => ({ ...current, [next.id]: next }));
  }, []);

  /*
   * A fresh list read supersedes every patch taken against the previous one.
   *
   * Done during render rather than in an effect so a superseded row is never
   * painted: React discards this pass and re-runs it, where an effect would
   * commit one frame showing a row the server has already corrected.
   *
   * ⚠️ **The identity check is exact, and that is what makes a FAILED re-read
   * behave correctly.** `useQuoteEnvelopes` keeps the previous array on an
   * error — the same array, not a copy — so a failure leaves the patches
   * standing, which is right: they are still the newest thing anybody has been
   * told about those rows.
   */
  if (patchedAgainst !== envelopes) {
    setPatchedAgainst(envelopes);
    setPatches({});
  }

  /*
   * ⚠️ **`envelopes ?? []` here is NOT the silent drop this codebase warns
   * about, and the distinction is the whole reason for the comment.** That rule
   * is about coalescing two facts that are both true and both need showing.
   * Here the two states are told apart ABOVE this list — `loading` and
   * `notWired` each own a render branch of their own — so by the time a list is
   * drawn, `null` and `[]` reach the same screen, exactly as they did when this
   * state was a non-nullable array in this file. The distinction still matters
   * to the quote header, which decides a CONTROL from it, which is why the hook
   * keeps it rather than flattening it at the source.
   */
  const rows = (envelopes ?? []).map((row) => patches[row.id] ?? row);

  /*
   * One live envelope per quote — a second send while one is outstanding is a
   * 409. Disabling ahead of it is an optimisation and the refusal is the
   * guarantee; the value here is that the reason is on screen rather than
   * discovered by pressing. Without it a double-clicked Send puts two contracts
   * in front of one customer, both equally legitimate-looking.
   */
  const outstanding = rows.find((envelope) => !envelope.terminal) ?? null;

  const sendDenial = !canManage
    ? (manageDenial ?? 'You do not have permission to send this for signature.')
    : outstanding
      ? 'A request is already outstanding — void it before sending another.'
      : null;

  const sendButton = onSend ? (
    <Button
      variant="primary"
      size="sm"
      disabled={sendDenial !== null}
      title={sendDenial ?? undefined}
      onClick={onSend}
    >
      Send for signature
    </Button>
  ) : null;

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        /*
         * ⚠️ **The `description` was REMOVED on the owner's instruction,
         * 2026-08-30**, along with the two notice boxes below. It carried
         * `STATUS_IS_LAST_KNOWN_NOTE` — that a status here is as of the last
         * check, because nothing in this browser polls.
         *
         * **That is still true.** Every timestamp on this panel is still worded
         * as *last checked* and never as *status*, and the per-row "Last
         * checked …" line still carries it where a reader meets a status. The
         * constant is untouched in `@/lib/docusign`; only this panel stopped
         * repeating it in a header. Do not restore it as an oversight.
         */
        title="Signature"
        actions={sendButton}
      />
      <PanelBody className="flex flex-col gap-3">
        {/*
         * ⚠️ **TWO NOTICE BOXES WERE REMOVED HERE on the owner's instruction,
         * 2026-08-30.** Both were standing explanatory blocks, not refusals, and
         * both are gone from the send screen too (`SendForSignatureModal`).
         * **The facts they carried are unchanged, which is the whole reason this
         * comment replaces them rather than nothing:**
         *
         * 1. `NotBindingWarning` — production runs the DocuSign **SANDBOX**
         *    deliberately, so every envelope raised against this quote is **not
         *    legally binding**, and **the failure mode looks exactly like
         *    success**: a signer completes it, it comes back marked signed, and
         *    it is worth nothing. That is still the case. `signaturesNotBinding()`
         *    in `@/lib/docusign` is untouched and is still the one rule; the
         *    send screen still renders its own sandbox warning, which is NOT
         *    part of this removal and must stay.
         *
         * 2. `AcceptanceConsequence` — a completed signature still **moves the
         *    quote to the accepted stage**, asking nobody, and the backend's
         *    status worker still polls outstanding envelopes on a timer, so it
         *    still happens **with nobody on this screen**. Acceptance moves the
         *    stage and nothing more; billing is a separate step on the quote.
         *
         * **Do not restore either box as an oversight.** Removing them was a
         * decision, not a sweep. Nothing below this line was removed with them:
         * the error blocks, the `403` branch and the Check-status denial reason
         * all report server responses and stay.
         */}

        {/*
         * Rendered ALONGSIDE whatever else holds, never instead of it — a stale
         * but true list and a failed re-read are both facts, and so are a failed
         * re-read and a failed check.
         */}
        {error && (
          <ErrorBlock title="Couldn’t read the signature requests for this quote.">
            {error}
          </ErrorBlock>
        )}
        {checkError && (
          <ErrorBlock title={checkError.title} note={checkError.advice}>
            {checkError.message}
          </ErrorBlock>
        )}

        {loading ? (
          <EnvelopeSkeletons />
        ) : notWired ? (
          <EmptyState
            icon={<EnvelopeIcon />}
            title="Signatures are not set up on the API yet"
            description="The API has no DocuSign endpoint to answer this yet."
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<EnvelopeIcon />}
            title="Nothing has been sent for signature"
            description={
              onSend
                /*
                 * ⚠️ This ended *"Read what a completed signature does, above,
                 * before you send — it is not a status change."* The box it
                 * pointed at was removed on 2026-08-30, so the clause was left
                 * naming nothing — a sentence losing its REFERENT rather than
                 * its accuracy, which still reads fine on its own and sends the
                 * reader hunting. Trimmed with the box, not separately.
                 */
                ? 'Sending puts this quote in front of a named signer at the customer.'
                : 'Nothing raised against this quote, and sending is not available here.'
            }
            action={
              sendButton && (
                <div className="flex flex-col items-center gap-2">
                  {sendButton}
                  {/*
                   * The reason is text as well as a `title`: a disabled button
                   * has `pointer-events: none`, so its tooltip never reaches
                   * the person it was written for.
                   */}
                  {sendDenial && <p className="m-0 text-xs text-fg-subtle">{sendDenial}</p>}
                </div>
              )
            }
          />
        ) : (
          <>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {rows.map((envelope) => (
                <EnvelopeRow
                  key={envelope.id}
                  envelope={envelope}
                  connection={connection}
                  canManage={canManage}
                  manageDenial={manageDenial ?? null}
                  checking={checkingId === envelope.id}
                  busy={checkingId !== null}
                  onCheck={() => void check(envelope)}
                  onRequestSigningLink={
                    onRequestSigningLink ? () => onRequestSigningLink(envelope) : undefined
                  }
                  onVoid={onVoid ? () => onVoid(envelope) : undefined}
                  onChangeSigner={
                    onChangeSigner ? () => setChangingSigner(envelope) : undefined
                  }
                  onReminded={replaceRow}
                />
              ))}
            </ul>
            {/*
             * ⛔ **The count-and-order line was removed — owner's helper-text
             * ruling, 2026-09-05.** It read `{N} signature requests, newest
             * first.` and sat under the list rather than in the header, where it
             * would have competed with the liveness sentence that has also now
             * gone.
             *
             * ⚠️ **The ORDER is still newest-first and is now unstated.** That is
             * the half worth noticing: a reader scanning for the live envelope
             * has to infer it from the dates on the rows.
             */}
          </>
        )}
      </PanelBody>

      {/*
        ⚠️ **The panel owns the VOID half of "change signer and resend", and only
        that half.** The dialog is the screen that states what voiding costs, so
        it belongs beside the envelope it is about; the SEND that follows needs
        the document payload — anchor text, the Xero fetch or an uploaded PDF —
        which only `SendForSignatureModal` knows, so the caller finishes it.

        `onChangeSigner` fires with the signer that was chosen, and the caller
        MUST pass it through as `initialSigner`. See the prop's own note: the send
        dialog re-seeds the company's primary contact into an untouched field, and
        that is often the exact person somebody has just decided not to send to.
      */}
      {changingSigner && onChangeSigner && (
        <ChangeSignerDialog
          open
          envelope={changingSigner}
          companyId={companyId}
          onClose={() => setChangingSigner(null)}
          onVoided={(voided, signer) => {
            // The row first, so the list agrees with the server before anything
            // opens on top of it — the envelope is `voided` now, and a stale row
            // beneath a send dialog reads as a request still outstanding.
            replaceRow(voided);
            setChangingSigner(null);
            onChangeSigner(voided, signer);
          }}
        />
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * One envelope
 * ---------------------------------------------------------------------- */

interface EnvelopeRowProps {
  envelope: DocuSignEnvelope;
  connection: DocuSignBindingSignals | null | undefined;
  canManage: boolean;
  manageDenial: string | null;
  checking: boolean;
  busy: boolean;
  onCheck: () => void;
  onRequestSigningLink?: () => void;
  onVoid?: () => void;
  /** Opens the change-signer dialog. Absent means the panel's caller cannot finish it. */
  onChangeSigner?: () => void;
  /** The envelope after a reminder, so the list holds the server's own copy. */
  onReminded: (envelope: DocuSignEnvelope) => void;
}

/**
 * A card rather than a table row, and that is a 320px decision: this many facts
 * and three actions cannot be a row on a phone, and this panel has no wide
 * variant to fall back to. It reads as one column below `sm` and pairs above it.
 *
 * ⚠️ The opening clause read *"nine facts and three actions"* until the signed
 * fields were added below. **A count in prose beside a list is the first thing
 * to rot and nothing fails when it does**, so it is removed rather than
 * corrected — count the `Fact`s in the source when the number is wanted.
 */
function EnvelopeRow({
  envelope,
  connection,
  canManage,
  manageDenial,
  checking,
  busy,
  onCheck,
  onRequestSigningLink,
  onVoid,
  onChangeSigner,
  onReminded,
}: EnvelopeRowProps) {
  /*
   * ⚠️ **Why a signing link cannot be minted, when it cannot — asked of
   * `signingUrlRefusal()` rather than re-derived here.** Two different refusals
   * hide behind one disabled button: an EMAILED envelope has no embedded link
   * and never will, so a retry cannot succeed and the recovery is a fresh
   * envelope; a TERMINAL one has nothing left to sign. Calling the endpoint to
   * find out is a wasted round trip that answers a 409 either way.
   *
   * `isEmbeddedDelivery` is the type-level half of the same question and is what
   * the signing-URL call demands, so the dialog cannot be handed an emailed
   * envelope even if this branch were wrong.
   */
  const refusal = signingUrlRefusal(envelope);
  const embedded = isEmbeddedDelivery(envelope);

  /*
   * What the customer typed on the document. Held as a value rather than read
   * inline twice, because the two nulls are answered by two different branches
   * below and reading `envelope.signedFields?.poNumber` at each site would
   * silently merge them — an optional chain cannot tell "we never asked" from
   * "they left it blank". See {@link DocuSignSignedFields}.
   */
  const signedFields: DocuSignSignedFields | null = envelope.signedFields;

  /*
   * Everyone this envelope was sent to.
   *
   * ⚠️ **`signers` is REQUIRED on the type and is still read defensively here**,
   * and that is not belt-and-braces — the two repos deploy independently, so a
   * browser running this build can be answered by an API that predates the field.
   * `.map` on `undefined` throws and takes the whole quote page down.
   *
   * ⚠️ **The fallback is `[signer]`, never `[]`.** `signer` IS `signers[0]` and
   * is present on every envelope this API has ever returned, so degrading to it
   * reproduces exactly the panel that shipped yesterday. `?? []` would render no
   * signer at all — the silent drop this repo writes up — and it cannot
   * under-report a multi-signer envelope, because a server with no `signers`
   * field cannot have created one.
   */
  const signers =
    Array.isArray(envelope.signers) && envelope.signers.length > 0
      ? envelope.signers
      : [{ recipientId: 1, name: envelope.signer.name, email: envelope.signer.email }];

  const showSigningLink = canManage && refusal === null && onRequestSigningLink !== undefined;
  const showVoid = canManage && !envelope.terminal && onVoid !== undefined;
  /*
   * ⚠️ **Changing the signer VOIDS**, so it is offered on exactly the envelopes
   * Void is offered on and hidden the moment one goes terminal — there is nothing
   * to replace on a finished envelope, and the recovery there is an ordinary
   * fresh Send.
   *
   * It sits beside Resend on purpose and the labels are deliberately not
   * variations on each other: one leaves the envelope, the customer's link and
   * any partial signature untouched, the other destroys all three. Somebody who
   * wants the same person chased must be able to tell at a glance which is which.
   */
  const showChangeSigner = canManage && !envelope.terminal && onChangeSigner !== undefined;

  /*
   * Why Check status is refused, when it is. Unlike Send and Void this control
   * stays RENDERED and disabled rather than disappearing — see the button — so
   * the sentence is what stops a disabled button reading as a broken one.
   *
   * The fallback matters: `manageDenial` is optional, and a caller that gates
   * without passing a reason would otherwise leave a dead button explaining
   * nothing, which is the exact failure `denialReason()` exists to prevent. It
   * is worded for the whole row because the same sentence also accounts for the
   * two controls that are absent.
   */
  const manageRefusal = canManage
    ? null
    : (manageDenial ?? 'You do not have permission to change this quote.');

  return (
    <li className="flex flex-col gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {/*
         * The badge owns the status word, its tone and the not-binding
         * qualification — `connection` is threaded through so it can qualify
         * `completed` rather than letting it read as an unqualified success.
         * Omitting it would warn anyway, but a caller that has the connection
         * and drops it is throwing away the one signal that removes the caveat
         * when real credentials land.
         */}
        <EnvelopeStatusBadge
          status={envelope.status}
          connection={connection}
          terminal={envelope.terminal}
          statusCheckedAt={envelope.statusCheckedAt}
        />
        {/*
         * `delivery` is stated on the row because it changes what a reader
         * should do next: an emailed signer already has the document, an
         * embedded one is waiting for somebody here to hand them a link.
         */}
        <Badge tone="neutral">{embedded ? 'Link handed over' : 'Emailed to signer'}</Badge>
      </div>

      {/*
        WHO WAS ASKED TO SIGN — every one of them.
        ⚠️ **An envelope may carry up to four signers since 2026-09-01**, and this
        block rendered `envelope.signer` alone, which is `signers[0]`. On a
        four-signer envelope that reported one name, correctly formatted, with
        nothing anywhere saying three people were missing — and this panel is
        where somebody checks who a contract went to.

        ⚠️ **One signer is unchanged, byte for byte.** A list of one renders the
        same line it always did; the ordinals appear only above one.
      */}
      <div className="grid min-w-0 gap-0.5">
        {signers.map((signer, index) => (
          <p
            key={index}
            className="m-0 min-w-0 text-sm font-medium text-fg [overflow-wrap:anywhere]"
          >
            {signers.length > 1 && (
              <span className="font-normal text-fg-subtle">{index + 1}. </span>
            )}
            {signer.name}
            {signer.email ? (
              <span className="font-normal text-fg-muted">
                {' · '}
                <EmailLink address={signer.email} tone="muted" />
              </span>
            ) : null}
          </p>
        ))}
      </div>

      {/*
        WHO ELSE WAS SENT IT. Owner instruction, 2026-09-16.

        ⚠️ **RENDERED ONLY WHEN THERE IS SOMETHING TO SAY, and the absence is
        deliberate rather than a layout saving.** `carbonCopies` is empty on
        every envelope sent before the feature existed — nothing backfills it
        and there is nothing to backfill from — so a permanent "Copied to: —"
        row would state *this went to nobody else* about hundreds of envelopes
        where nothing was ever recorded either way. There is no flag separating
        the two and there cannot be one. Saying nothing is the only honest
        answer for both populations.

        ⚠️ **"Copied when this was sent", NOT "copied to".** The row records what
        EMERGE sent; a recipient added or removed inside DocuSign afterwards is
        not reflected here, and the past tense is what keeps this from reading
        as the current recipient list.

        ⚠️ **THE EMAIL ALONE, never the name beside it.** A copy row asks for an
        address and the server sends that address as the DocuSign name, so
        `name` is routinely identical to `email` — rendering the signer shape
        here would print the same string twice.

        ⚠️ `?? []` AT THE BOUNDARY AND NOWHERE DEEPER. The key is optional on
        the type only because this app can be deployed ahead of the API; the
        server always sends it. Nothing below may branch on `undefined` as
        though it were a third state.
      */}
      {(envelope.carbonCopies ?? []).length > 0 && (
        <p className="m-0 min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
          Copied when this was sent:{' '}
          {(envelope.carbonCopies ?? []).map((cc, index) => (
            <Fragment key={`${index}-${cc.email}`}>
              {index > 0 ? ', ' : ''}
              <EmailLink address={cc.email} tone="muted" />
            </Fragment>
          ))}
        </p>
      )}

      {/*
       * ⚠️ **Worded as a CHECK, never as a status.** "Last checked" and "status
       * as of" are different claims, and only one of them is true here: nobody
       * is watching, and this is simply the last time anyone asked. The
       * never-checked case gets a sentence rather than a dash, because a dash
       * would read as missing data instead of as a fact about this envelope.
       */}
      <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
        {checkedLabel(envelope.statusCheckedAt)}
      </p>

      <dl className="m-0 grid gap-2 sm:grid-cols-2">
        <Fact label="Sent">{dateOrDash(envelope.sentAt)}</Fact>
        {envelope.completedAt ? (
          <Fact label="Signed">{formatDateTime(envelope.completedAt)}</Fact>
        ) : envelope.declinedAt ? (
          <Fact label="Declined">{formatDateTime(envelope.declinedAt)}</Fact>
        ) : envelope.voidedAt ? (
          <Fact label="Voided">{formatDateTime(envelope.voidedAt)}</Fact>
        ) : (
          <Fact label="Sent by">{envelope.createdBy?.name ?? '—'}</Fact>
        )}
        {/*
         * DocuSign's own id, and the only thing worth quoting in a support
         * ticket — ours means nothing to them. Monospace because it is copied,
         * not read.
         */}
        <Fact label="DocuSign id">
          <span className="font-mono text-2xs">{envelope.envelopeId}</span>
        </Fact>
        {/* The signer reads this, so it is shown rather than summarised. */}
        {envelope.voidedReason && <Fact label="Reason given">{envelope.voidedReason}</Fact>}

        {/*
         * WHAT THE CUSTOMER TYPED ON THE DOCUMENT — read-only, and read back
         * out of DocuSign rather than entered anywhere here. There is no input,
         * no edit affordance and there must never be one: these are a record of
         * a signed contract.
         *
         * ⚠️ **THREE STATES, and the first two are the ones that get collapsed.**
         *
         *   signedFields present   render each box; a null KEY means the signer
         *                          left it blank, which is ordinary — both
         *                          boxes are optional — and is worded as such
         *   signedFields null,     nothing has been read back YET. A different
         *     envelope signed      fact entirely, so it gets its own sentence
         *                          rather than three blank-looking rows
         *   signedFields null,     nothing rendered. An envelope nobody has
         *     not signed           signed has no answers, and an empty labelled
         *                          section is a claim that the customer
         *                          answered nothing
         *
         * Rendering an em dash for the first two is the failure this shape
         * exists to avoid — see {@link DocuSignSignedFields}.
         */}
        {signedFields ? (
          <>
            <Fact label="PO number">{typedValue(signedFields.poNumber)}</Fact>
            <Fact label="Invoice instructions">
              {typedValue(signedFields.invoiceInstructions)}
            </Fact>
            {/*
             * DocuSign's own auto-filled printed name, NOT `signer.name` above,
             * which is what we sent. Labelled so the two are not read as one
             * field repeated.
             */}
            <Fact label="Printed name">{typedValue(signedFields.name)}</Fact>
            {/*
             * Staleness for these three, worded the way every other timestamp
             * on this panel is: when somebody last ASKED, never "as of now".
             * Omitted rather than dashed when absent — there is no read time to
             * report on values that arrived without one.
             */}
            {signedFields.readAt && (
              <Fact label="Details read">{formatDateTime(signedFields.readAt)}</Fact>
            )}
          </>
        ) : envelope.signed ? (
          /*
           * The explanation goes on a `title` rather than into a second
           * sentence on screen — the local convention for a state that needs
           * qualifying, and the owner has asked twice for no prose.
           */
          <Fact label="Signed details">
            <span title="Nothing has been read back from DocuSign for this envelope yet. It does not mean the signer left the boxes empty.">
              Not read back from DocuSign yet
            </span>
          </Fact>
        ) : null}
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        {/*
         * The explicit refresh, and the only thing on this panel that changes a
         * status. Offered on terminal envelopes too — see `check()` for why
         * that is the repair rather than a pointless request.
         *
         * ⚠️ **GATED as of 2026-08-29, REVERSING a decision recorded here. Read
         * this as a decision record, not as a rule.**
         *
         * What stood here was: *"UNGATED ON PURPOSE, and it is the only control
         * here that is … it was put to the owner and he decided to leave it
         * open"*, on the owner's reasoning that **the viewer is not authoring
         * the decision — the customer signed**, and that gating it would leave a
         * signed quote unaccepted until a write-role user happened to look. It
         * added that *"the server agrees: `POST
         * /docusign/envelopes/{id}/refresh` carries no role guard, so gating
         * here would make this app stricter than the API."*
         *
         * ⚠️ **The server no longer agrees.** Backend release
         * `20260829123506-c323947` put `WRITE_ROLES` on that route, so it is a
         * `403` for a `viewer`. Leaving the button enabled would not preserve
         * the old behaviour — it would hand the one role that cannot succeed the
         * only control that looks like it can.
         *
         * ⚠️ **The reversal was deliberate and the reason is the part worth
         * keeping.** The gate was open because observing a completed envelope
         * raised an invoice that was going to be raised anyway — the observation
         * was worth more than the write. Release `20260829011913-5fbab06` then
         * removed the invoice and the Xero push, so **acceptance now moves the
         * stage and nothing else**, which killed that argument: what was left
         * was a route writing a stage move and a Xero attachment for a caller
         * with no write role. A decision is only as good as the cost it was
         * weighed against, and the cost moved.
         *
         * ⚠️ **THE PRICE OF THE REVERSAL WAS PAID BY THE BACKEND, 2026-08-30,
         * and the old paragraph here is now wrong.** It read: *"There is still
         * no webhook. So a quote a customer has signed now sits unaccepted
         * until somebody with a write role opens it and presses this. Before
         * today a `viewer` advanced it simply by looking. Nothing in this app
         * can close that gap."* The last clause was right — nothing in this app
         * could. The backend closed it instead, with a status worker that runs
         * this same route on a timer, no role and nobody at a screen. So gating
         * this button no longer strands a signed quote.
         *
         * ⚠️ **That does NOT make this button redundant, and it does not make
         * anything live.** The poll is periodic, backs off after a failure, and
         * does not run where DocuSign is unconfigured; this is how somebody gets
         * an answer *now*. No copy here may imply a signature takes effect on
         * its own — but state the true reason (**the row is a snapshot and the
         * next pass is not guaranteed**), not the spent one (*"nothing happens
         * without a person"*). See `BACKGROUND_CHECK_NOTE` in `@/lib/docusign`.
         *
         * ⚠️ **Disabled, not absent** — the opposite of the Xero `sync` rule,
         * and for the opposite reason. There the only outcome was an error
         * whoever pressed it; here the action is real and the refusal is about
         * *this reader*, so removing the button would make a permission look
         * like a missing feature. `manageRefusal` is on screen as text as well
         * as on the `title`, because a disabled button has `pointer-events:
         * none` and its tooltip never reaches the person it was written for.
         *
         * ⚠️ **The gate is the optimisation; the `403` is the guarantee.** A
         * role can be taken away under a live access token — see `check()`.
         */}
        <Button
          size="sm"
          loading={checking}
          disabled={busy || manageRefusal !== null}
          title={manageRefusal ?? undefined}
          onClick={onCheck}
        >
          Check status
        </Button>
        {/*
         * ⚠️ **RENDERED ON EVERY ROW, gated INSIDE the control, and disabled
         * with a reason rather than removed.** The reasons it can be off are
         * three different facts — no write role, the envelope is finished, or it
         * was delivered as a link so DocuSign never emailed anybody — and an
         * absent button makes all three look like a missing feature. That is the
         * `Check status` treatment, not the Xero `sync` one: the action is real,
         * and the refusal is about this envelope or this reader.
         *
         * ⚠️ **The COOLDOWN is not visible from here and cannot be.** Nothing on
         * a `GET /docusign/envelopes` row carries `lastRemindedAt` into this app,
         * so a just-reminded envelope looks remindable and the `429` is where
         * anybody finds out — the accepted contract, the same shape as the
         * company-approval resend. The control turns that refusal into a live
         * countdown rather than an error.
         */}
        <RemindEnvelopeControl
          envelope={envelope}
          canManage={canManage}
          manageDenial={manageDenial}
          busy={busy}
          onReminded={onReminded}
        />
        {showSigningLink && (
          <Button size="sm" disabled={busy} onClick={onRequestSigningLink}>
            Get signing link…
          </Button>
        )}
        {showChangeSigner && (
          <Button size="sm" variant="danger" disabled={busy} onClick={onChangeSigner}>
            Change signer and resend…
          </Button>
        )}
        {showVoid && (
          <Button size="sm" variant="danger" disabled={busy} onClick={onVoid}>
            Void…
          </Button>
        )}
      </div>

      {/*
       * Said once, on the row that offers the link, rather than in the dialog
       * alone: someone deciding whether to press it needs to know it is not a
       * link they can save for later or send on.
       */}
      {showSigningLink && (
        <p className="m-0 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
          Single use, lasts about five minutes. Mint it with the signer present.
        </p>
      )}

      {/*
       * ⛔ **THE EMAILED-CASE SENTENCE WAS REMOVED — owner's helper-text ruling,
       * 2026-09-05.** Gated on `refusal === 'emailed' && !envelope.terminal`, it
       * read: *"Emailed to the signer, so there is no link. Void this and send a
       * fresh request as a link."*
       *
       * ⚠️ **It existed so a MISSING BUTTON would not be puzzled over**, and it
       * named the only recovery there is — a fresh envelope. *"Try again"* would
       * have been advice that cannot be followed: the link does not exist and
       * never will for this envelope. **That absent control is now unexplained.**
       *
       * The terminal case never had a sentence and still does not, for a reason
       * unaffected by the ruling: the status badge beside it already says the
       * envelope is finished.
       */}

      {/*
       * Why the controls on this row are refused. It accounts for all four at
       * once: Send, the signing link and Void are absent, and — since
       * 2026-08-29 — Check status is present and disabled. A disabled button
       * with no reason beside it is indistinguishable from a broken one, which
       * is the whole job of `denialReason()` upstream.
       */}
      {manageRefusal && (
        <p className="m-0 text-2xs text-fg-subtle [overflow-wrap:anywhere]">{manageRefusal}</p>
      )}
    </li>
  );
}
